import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import { Plus, Package, Pencil, Trash2 } from "lucide-react";

export default function Products() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const showPrices = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState("");
  const [form, setForm] = useState({ name:"", sku:"", category_id:"", brand_id:"", model_id:"", uom:"Nos", serialized:false, min_stock:0, rate:0, description:"" });

  const load = () => Promise.all([
    api.get("/products"), api.get("/categories"), api.get("/brands"), api.get("/models"),
  ]).then(([p,c,b,m]) => { setItems(p.data); setCats(c.data); setBrands(b.data); setModels(m.data); });
  useEffect(() => { load().catch(e=>setErr(formatApiError(e))); }, []);

  const filtered = search ? items.filter(i =>
    (i.name||"").toLowerCase().includes(search.toLowerCase()) ||
    (i.sku||"").toLowerCase().includes(search.toLowerCase())
  ) : items;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, min_stock: parseInt(form.min_stock)||0, rate: parseFloat(form.rate)||0 };
      if (editId) {
        await api.patch(`/products/${editId}`, payload);
      } else {
        await api.post("/products", payload);
      }
      setShowCreate(false); setEditId("");
      setForm({ name:"", sku:"", category_id:"", brand_id:"", model_id:"", uom:"Nos", serialized:false, min_stock:0, rate:0, description:"" });
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name || "", sku: p.sku || "",
      category_id: p.category_id || "", brand_id: p.brand_id || "", model_id: p.model_id || "",
      uom: p.uom || "Nos", serialized: !!p.serialized,
      min_stock: p.min_stock ?? 0, rate: p.rate ?? 0, description: p.description || "",
    });
    setShowCreate(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deactivate = async (p) => {
    if (!window.confirm(`Deactivate "${p.name}"? It will no longer be selectable.`)) return;
    try { await api.delete(`/products/${p.id}`); load(); }
    catch (e2) { setErr(formatApiError(e2)); }
  };

  const filteredBrands = form.category_id ? brands.filter(b => b.category_id === form.category_id) : brands;
  const filteredModels = form.brand_id ? models.filter(m => m.brand_id === form.brand_id) : [];

  return (
    <div className="p-4 md:p-8" data-testid="products-page">
      <PageHeader title="Product Master" subtitle="Category → Brand → Model. Serialized items track individual units."
        action={canEdit ?
          <button onClick={()=>{ setShowCreate(s=>!s); setEditId(""); setForm({ name:"", sku:"", category_id:"", brand_id:"", model_id:"", uom:"Nos", serialized:false, min_stock:0, rate:0, description:"" }); }} data-testid="add-product-btn" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">
            <Plus size={14}/> {showCreate && !editId ? "Cancel" : "Add Product"}
          </button> : null
        }
      />

      {showCreate && (
        <Card className="p-5 mb-6" data-testid="product-form">
          <div className="font-display font-semibold mb-3">{editId ? "Edit Product" : "Add Product"}</div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select required value={form.category_id} onChange={e=>setForm(f=>({...f, category_id:e.target.value, brand_id:"", model_id:""}))} className="inp" data-testid="prod-category">
              <option value="">Category</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select required value={form.brand_id} onChange={e=>setForm(f=>({...f, brand_id:e.target.value, model_id:""}))} className="inp" data-testid="prod-brand">
              <option value="">Brand</option>
              {filteredBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select required value={form.model_id} onChange={e=>setForm(f=>({...f, model_id:e.target.value}))} className="inp" data-testid="prod-model">
              <option value="">Model</option>
              {filteredModels.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <input required placeholder="Product Name" value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} className="inp" data-testid="prod-name" />
            <input required placeholder="SKU" value={form.sku} onChange={e=>setForm(f=>({...f, sku:e.target.value}))} className="inp" data-testid="prod-sku" />
            <input placeholder="UoM" value={form.uom} onChange={e=>setForm(f=>({...f, uom:e.target.value}))} className="inp" />
            <input type="number" placeholder="Min Stock" value={form.min_stock} onChange={e=>setForm(f=>({...f, min_stock:e.target.value}))} className="inp" />
            {showPrices && <input type="number" placeholder="Rate (₹)" value={form.rate} onChange={e=>setForm(f=>({...f, rate:e.target.value}))} className="inp" />}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.serialized} onChange={e=>setForm(f=>({...f, serialized:e.target.checked}))} data-testid="prod-serialized" /> Serialized
            </label>
            <button type="submit" data-testid="prod-save-btn" className="md:col-span-3 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">{editId ? "Update" : "Save"}</button>
          </form>
        </Card>
      )}

      <div className="mb-3"><input data-testid="prod-search" placeholder="Search by name or SKU..." value={search} onChange={e=>setSearch(e.target.value)} className="inp max-w-sm" /></div>
      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}

      {filtered.length === 0 ? <EmptyState title="No products" icon={Package} /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">SKU</th>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Brand</th>
                  <th className="px-4 py-3 text-left">Model</th>
                  <th className="px-4 py-3 text-left">UoM</th>
                  <th className="px-4 py-3 text-left">Serialized</th>
                  <th className="px-4 py-3 text-right">Min</th>
                  {showPrices && <th className="px-4 py-3 text-right">Rate</th>}
                  {canEdit && <th className="px-4 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className={`border-t border-zinc-100 hover:bg-zinc-50 ${p.active === false ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 num font-semibold">{p.sku}</td>
                    <td className="px-4 py-3">{p.name}{p.active === false && <span className="ml-2 text-[10px] uppercase font-bold text-zinc-400">(inactive)</span>}</td>
                    <td className="px-4 py-3 text-zinc-500">{p.category_name}</td>
                    <td className="px-4 py-3 text-zinc-500">{p.brand_name}</td>
                    <td className="px-4 py-3 text-zinc-500">{p.model_name}</td>
                    <td className="px-4 py-3">{p.uom}</td>
                    <td className="px-4 py-3">{p.serialized ? "Yes" : "No"}</td>
                    <td className="px-4 py-3 num text-right">{p.min_stock}</td>
                    {showPrices && <td className="px-4 py-3 num text-right">₹{p.rate}</td>}
                    {canEdit && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={()=>startEdit(p)} data-testid={`edit-prod-${p.sku}`} className="text-xs font-semibold text-[#F97316] hover:underline mr-3 inline-flex items-center gap-1"><Pencil size={12}/> Edit</button>
                        {p.active !== false && (
                          <button onClick={()=>deactivate(p)} data-testid={`deactivate-prod-${p.sku}`} className="text-xs font-semibold text-rose-600 hover:underline inline-flex items-center gap-1"><Trash2 size={12}/> Deactivate</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}
